import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "../app/router";

type Kind = "default" | "primary" | "pond" | "ghost" | "danger";

const cls = (...parts: (string | false | undefined)[]): string => parts.filter(Boolean).join(" ");

export function Button({ kind = "default", size, className, ...rest }: { kind?: Kind; size?: "small" | "big" } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={cls("btn", kind !== "default" && `btn-${kind}`, size, className)} {...rest} />;
}

export function LinkButton({ to, kind = "default", size, className, children, ...rest }: { to: string; kind?: Kind; size?: "small" | "big"; className?: string; children: ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <Link to={to} className={cls("btn", kind !== "default" && `btn-${kind}`, size, className)} {...rest}>
      {children}
    </Link>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="spinner-dot" />
      <span className="spinner-dot" />
      <span className="spinner-dot" />
      {label && <span className="muted">{label}</span>}
    </div>
  );
}

// ---- toasts ----

type ToastKind = "info" | "error";
const ToastCtx = createContext<(msg: string, kind?: ToastKind) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastHost({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<{ id: number; msg: string; kind: ToastKind }[]>([]);
  const nextId = useRef(1);
  const push = useCallback((msg: string, kind: ToastKind = "info") => {
    const id = nextId.current++;
    setItems((cur) => [...cur.slice(-2), { id, msg, kind }]);
    setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== id)), 3800);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cls("toast", t.kind === "error" && "toast-error")} role="status">
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---- ring ----

export function Ring({ value, max, size = 120, stroke = 10, done, children }: { value: number; max: number; size?: number; stroke?: number; done?: boolean; children?: ReactNode }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, max ? value / max : 0));
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line-2)" strokeWidth={stroke} />
        {frac > 0 && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
            stroke={done ? "var(--leaf)" : "var(--koi)"}
            strokeDasharray={`${c * frac} ${c}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dasharray 0.6s ease, stroke 0.3s ease" }}
          />
        )}
      </svg>
      <div className="ring-center">{children}</div>
    </div>
  );
}

// ---- modal ----

export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title?: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    // focus the dialog itself, not a field: a text box would pop up the keyboard on phones and scroll the page
    ref.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={cls("modal card", wide && "wide")} role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <button type="button" className="btn btn-ghost icon-btn small modal-x" onClick={onClose} aria-label="Close">
          ×
        </button>
        {children}
      </div>
    </div>
  );
}
